import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Building2, UserRoundMinus, UserRoundPlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function Block({ title, icon: Icon, count, children, testId }) {
  return (
    <section className="connecteam-panel" data-testid={testId}>
      <div className="section-heading">
        <div><h3><Icon size={15} /> {title} <span className="status-chip status-pending" data-testid={`${testId}-count`}>{count}</span></h3></div>
      </div>
      <div className="requests-table-panel">{children}</div>
    </section>
  );
}

export function RosterReconciliationPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setData((await api.get("/connecteam/roster-reconciliation")).data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <p className="form-message error-message" data-testid="roster-error">{error}</p>;
  if (!data) return <p className="projection-loading">Comparing rosters…</p>;

  return (
    <div className="connecteam-roster" data-testid="connecteam-roster">
      <p className="pairing-meta">Compares the current Connecteam snapshot with TAGForce providers and their assignments (worked sites from the last {data.window_days} days). Fix mappings on the User mapping tab; fix schedules and clock-ins in Connecteam.</p>
      <Block title="Active in Connecteam, not in TAGForce" icon={UserRoundPlus} count={data.connecteam_only.length} testId="roster-connecteam-only">
        <table className="requests-table connecteam-table">
          <thead><tr><th>Connecteam user</th><th>Title · sites</th><th>Email</th><th>Why</th></tr></thead>
          <tbody>
            {data.connecteam_only.map((u) => <tr key={u.connecteam_user_id} data-testid={`roster-ct-${u.connecteam_user_id}`}><td><strong>{u.first_name} {u.last_name}</strong></td><td>{u.title || "—"}{u.sites?.length ? ` · ${u.sites.join(", ")}` : ""}</td><td>{u.email || "—"}</td><td>{u.reason}{u.mapped_name ? ` (${u.mapped_name})` : ""}</td></tr>)}
            {!data.connecteam_only.length && <tr><td colSpan={4} className="provider-empty-state">Everyone active in Connecteam is mapped to an active TAGForce account.</td></tr>}
          </tbody>
        </table>
      </Block>
      <Block title="Active in TAGForce, not in Connecteam" icon={UserRoundMinus} count={data.tagforce_only.length} testId="roster-tagforce-only">
        <table className="requests-table connecteam-table">
          <thead><tr><th>Provider</th><th>Discipline</th><th>Email</th><th>Why</th></tr></thead>
          <tbody>
            {data.tagforce_only.map((p) => <tr key={p.id} data-testid={`roster-tf-${p.id}`}><td><strong>{p.name}</strong></td><td>{p.discipline || "—"}</td><td>{p.email?.endsWith("@pending.invalid") ? <span className="pairing-meta">no email on file</span> : p.email}</td><td>{p.reason}</td></tr>)}
            {!data.tagforce_only.length && <tr><td colSpan={4} className="provider-empty-state">Every active provider has an active Connecteam user.</td></tr>}
          </tbody>
        </table>
      </Block>
      <Block title="Scheduled or worked sites that do not match the TAGForce assignment" icon={Building2} count={data.site_mismatches.length} testId="roster-site-mismatches">
        <table className="requests-table connecteam-table">
          <thead><tr><th>Provider</th><th>Connecteam site</th><th>Mapped client</th>{!data.hours_redacted && <th>Hours (window)</th>}<th>TAGForce assignment</th></tr></thead>
          <tbody>
            {data.site_mismatches.map((m) => <tr key={`${m.provider_id}-${m.job_id}`} data-testid={`roster-mismatch-${m.provider_id}-${m.job_id}`}><td><strong>{m.provider_name}</strong></td><td>{m.job_title}</td><td>{m.client_name}</td>{!data.hours_redacted && <td>{m.hours}</td>}<td className="flag-text"><AlertTriangle size={12} /> {m.assigned_clients.length ? m.assigned_clients.join(", ") : "No client assignment in TAGForce"}</td></tr>)}
            {!data.site_mismatches.length && <tr><td colSpan={5} className="provider-empty-state">All worked sites match the providers' TAGForce assignments.</td></tr>}
          </tbody>
        </table>
        {data.unmapped_sites.length > 0 && (
          <details className="supervision-history" data-testid="roster-unmapped-sites">
            <summary>{data.unmapped_sites.length} worked site(s) not yet mapped to a TAGForce client — map or ignore them on the User mapping tab</summary>
            <ul className="month-requirements">{data.unmapped_sites.map((m) => <li key={`${m.provider_id}-${m.job_id}`}>{m.provider_name} · {m.job_title}{data.hours_redacted ? "" : ` · ${m.hours} h`}</li>)}</ul>
          </details>
        )}
      </Block>
    </div>
  );
}
